import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { listPublishedReports, type ReportRow } from "@/lib/reports.functions";
import { buildEnTopics, enTopicSlug, trTopicSlug } from "@/lib/en-topics";
import { getTag, TAGS } from "@/lib/tags";
import { pageTitle } from "@/lib/brand";
import { breadcrumbSchema, EN_HOME } from "@/lib/en-schema";

const EN = "https://smartsustainability.ai";
const TR = "https://akillisurdurulebilirlik.com";
const KIND: Record<string, string> = { idea: "Essay", cop31: "COP31 note", report: "Report" };

export const Route = createFileRoute("/topics/$slug")({
  loader: async ({ params, context }) => {
    // Turkish tag slugs (e.g. from the language toggle) 301 to the English slug.
    if (getTag(params.slug) && enTopicSlug(params.slug) !== params.slug) {
      throw redirect({ to: "/topics/$slug", params: { slug: enTopicSlug(params.slug) }, statusCode: 301 });
    }
    const trSlug = trTopicSlug(params.slug);
    const tag = trSlug ? getTag(trSlug) : undefined;
    if (!tag) throw notFound();
    const reports = (await context.queryClient.ensureQueryData({
      queryKey: ["public-reports"],
      queryFn: () => listPublishedReports(),
    })) as ReportRow[];
    const topics = buildEnTopics(reports ?? []);
    const items = topics.get(tag.slug);
    if (!items) throw notFound();
    const others = TAGS.filter((t) => t.slug !== tag.slug && topics.has(t.slug)).map((t) => ({
      slug: enTopicSlug(t.slug),
      en: t.en,
    }));
    return { tag, items, others };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: pageTitle("Topic not found", "en") }, { name: "robots", content: "noindex" }] };
    }
    const { tag, items } = loaderData;
    const url = `${EN}/topics/${enTopicSlug(tag.slug)}`;
    const desc = `${tag.descEn} ${items.length} essays, COP31 notes and reports.`;
    return {
      meta: [
        { title: pageTitle(tag.en, "en") },
        { name: "description", content: desc },
        { property: "og:title", content: `${tag.en} — Smart Sustainability` },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hreflang: "en", href: url },
        { rel: "alternate", hreflang: "tr", href: `${TR}/etiket/${tag.slug}` },
        { rel: "alternate", hreflang: "x-default", href: `${TR}/etiket/${tag.slug}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: EN_HOME },
              { name: "Topics", item: `${EN}/topics` },
              { name: tag.en, item: url },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-3xl mx-auto px-6 py-24">
      <h1 className="font-serif text-3xl">Topic not found</h1>
      <Link to="/topics" className="mt-6 inline-block text-clay underline">
        All topics
      </Link>
    </div>
  ),
  component: TopicPage,
});

function TopicPage() {
  const { tag, items, others } = Route.useLoaderData();
  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
      <div className="max-w-3xl mb-10">
        <Link to="/topics" className="text-clay text-xs uppercase tracking-[0.25em] font-medium hover:underline">
          Topics
        </Link>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02] tracking-tight">{tag.en}</h1>
        <p className="mt-5 text-ink/70 leading-relaxed">{tag.descEn}</p>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/60">{items.length} entries</p>
      </div>
      <ul className="divide-y divide-border">
        {items.map((a) => (
          <li key={a.href}>
            <a href={a.href} className="grid md:grid-cols-12 gap-6 py-8 group items-baseline">
              <div className="md:col-span-3 text-[10px] uppercase tracking-[0.2em] text-clay">{KIND[a.kind]}</div>
              <div className="md:col-span-9">
                <h2 className="font-serif text-2xl leading-snug group-hover:text-clay transition-colors text-balance">
                  {a.title}
                </h2>
                {a.summary && (
                  <p className="mt-2 text-sm text-ink/70 leading-relaxed line-clamp-3">{a.summary}</p>
                )}
              </div>
            </a>
          </li>
        ))}
      </ul>
      {others.length > 0 && (
        <div className="mt-16 pt-8 border-t border-border">
          <p className="text-[10px] uppercase tracking-[0.25em] text-ink/60 mb-4">Other topics</p>
          <div className="flex flex-wrap gap-2">
            {others.map((t) => (
              <Link
                key={t.slug}
                to="/topics/$slug"
                params={{ slug: t.slug }}
                className="px-3.5 py-1.5 rounded-full border border-ink/20 text-[10px] uppercase tracking-[0.2em] font-semibold text-ink/75 hover:border-ink hover:text-ink transition-colors"
              >
                {t.en}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
